import * as Box from './boxes';
import { createIndexArray } from './titles';
import {
  ItemTypes,
  colId,
  defaultItemValues,
  variableHandlingType,
} from '../constants/constants';
import {
  ContentState,
  convertFromRaw,
  convertToRaw,
  EditorState,
  genKey,
  Modifier,
  SelectionState,
} from 'draft-js';
import _ from 'lodash';
import { stateToHTML } from 'draft-js-export-html';
import i18n from '../i18n';
import {
  decorator,
  injectVariableInEditorState,
  replaceStyle,
  convertContentToRawText,
} from '../components/RichEditor/utils';
import { pageHeight, pageWidth } from '../constants/gridConfig';
import { newContainer } from './containers';
import { newColumn } from './columns';
import { variableIsAnImage } from '../components/RightSide/variablesUtils';
import { getVariablesGroupedByContainer, replaceVariables } from './variables';
import { rawVariableGroupToStoreVariableGroup } from './variableGroups';
import { attachAddressGroups } from './address';
import { hydrateShapeTexts, serializeShapeTexts } from '../components/Shape/shapeText';
import { withoutPageBackgroundFill } from './pageBackgroundFill';
import {
  isAMultiTextBox,
  MULTI_TEXT_CONTENT_KEYS,
} from '../shared/boxes/multiTextBoxes';

export const findWithRegex = (regex, text) => {
  let matchArr, start, end;
  matchArr = regex.exec(text);
  if (!matchArr) return null;
  start = matchArr.index;
  end = start + matchArr[0].length;
  return { start, end, label: matchArr[0] };
};

export const findAllWithRegex = (regex, text) => {
  const occurences = [];
  let textToParse = text;
  let parsedChars = 0;
  while (regex.exec(textToParse)) {
    let matchArr, start, end, readLength;
    matchArr = regex.exec(textToParse);
    readLength = matchArr.index + matchArr[0].length;
    start = parsedChars + matchArr.index;
    end = start + matchArr[0].length;
    occurences.push({ start, end, label: matchArr[0] });
    textToParse = textToParse.slice(readLength);
    parsedChars += readLength;
  }
  return occurences;
};

export const replacePlaceholderTable = (content, variables, documentType) => {
  content.columns.forEach((elementColumn, indexColumn) => {
    content.columns[indexColumn].title = JSON.stringify(
      convertToRaw(
        replaceVariables(
          transformTextToEditorState(content.columns[indexColumn].title),
          variables
        ).getCurrentContent()
      )
    );
    content.data.forEach((element, index) => {
      content.data[index][elementColumn['key']] = JSON.stringify(
        convertToRaw(
          replaceVariables(
            transformTextToEditorState(
              content.data[index][elementColumn['key']]
            ),
            variables
          ).getCurrentContent()
        )
      );
    });
  });
  (content.columnsDisposition || []).forEach((elementColumn, indexColumn) => {
    content.columnsDisposition[indexColumn].title = JSON.stringify(
      convertToRaw(
        replaceVariables(
          transformTextToEditorState(
            content.columnsDisposition[indexColumn].title
          ),
          variables
        ).getCurrentContent()
      )
    );
  });
  return content;
};

export const replaceNameAndLabelVariable = (variableBox, variables) => {
  const newVariableBox = { ...variableBox };
  const selectionKey = Box.getVariableSelectionKey(newVariableBox.type);

  newVariableBox.name = replaceVariablesInPlainText(
    newVariableBox.name,
    variables
  );

  if (selectionKey) {
    newVariableBox[selectionKey] = newVariableBox[selectionKey].map(
      (option) => ({
        ...option,
        label: replaceVariablesInPlainText(option.label, variables),
      })
    );
  }

  return newVariableBox;
};

export const replaceVariable = (text, variables, foundVariableInText) => {
  const offerKeyIfOffer = variables.offer_variables ? 'offer_variables.' : '';
  const variableReplacement = getVariableReplacement(
    foundVariableInText.label,
    offerKeyIfOffer,
    variables
  );
  return text.replaceAll(foundVariableInText.label, variableReplacement);
};

export const replaceVariablesInPlainText = (text, variables) => {
  const regex = new RegExp(/#{.*?}/, 'm');
  let nextText = text;
  const variablesInText = findAllWithRegex(regex, nextText);
  variablesInText.reverse().forEach((foundVariableInText) => {
    nextText = replaceVariable(nextText, variables, foundVariableInText);
  });
  return nextText;
};

export const getVariableReplacement = (
  variableLabel,
  offerKeyIfOffer,
  variables
) => {
  let place = offerKeyIfOffer + variableLabel.slice(2, -1);
  const variableFounded = _.get(variables, place);
  const variableReplacement =
    typeof variableFounded === 'object'
      ? variableFounded.value
      : variableFounded;
  if (!variableReplacement) {
    return variableLabel;
  } else if (typeof variableReplacement === 'number') {
    return variableReplacement.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  }
  return variableReplacement;
};

export const replaceVariablesInEditorState = (
  editorState,
  variables,
  currentDocIsTemplate
) => {
  const regex = new RegExp(/#{.*?}/, 'm');
  let nextEditorState = editorState;
  let blockMap = nextEditorState.getCurrentContent().getBlockMap();
  // en inversant l'ordre des block et des variables lues dans les blocs
  // on se prémunit des effets de la modification de la longueur de la
  // chaine de caractère sur les index
  blockMap.reverse().forEach((contentBlock) => {
    const variablesInBlock = findAllWithRegex(regex, contentBlock.getText());
    variablesInBlock.reverse().forEach((foundVariableInText) => {
      nextEditorState = injectVariableInEditorState({
        blockKey: contentBlock.getKey(),
        editorState: nextEditorState,
        variableKey: foundVariableInText.label.substring(
          2,
          foundVariableInText.label.length - 1
        ),
        variablesInStore: variables,
        isTemplate: currentDocIsTemplate,
        startOffset: foundVariableInText.end,
        anchorOffset: foundVariableInText.start,
      });
    });
  });
  return nextEditorState;
};

const contentToRaw = (contentText) => {
  let newContent = { ...contentText };
  newContent = {
    ...newContent,
    content: {
      ...newContent.content,
      raw: JSON.stringify(
        convertToRaw(newContent.content.editorState.getCurrentContent())
      ),
      editorState: null,
      text: null,
    },
  };
  return newContent;
};

/**
 * Box à plusieurs textes riches (Volet, Carte info…) : chaque clé se
 * sérialise comme un texte ordinaire, `<clé>EditorState` → `<clé>Raw`.
 * Cf. MULTI_TEXT_CONTENT_KEYS, qui fait foi sur la liste des textes.
 */
const multiTextContentToRaw = (box) => {
  const content = { ...box.content };
  MULTI_TEXT_CONTENT_KEYS[box.type].forEach((key) => {
    const editorState = content[`${key}EditorState`];
    content[`${key}Raw`] = editorState
      ? JSON.stringify(convertToRaw(editorState.getCurrentContent()))
      : null;
    content[`${key}EditorState`] = null;
  });
  return { ...box, content };
};

export const triggersToRaw = (triggers, boxes) => {
  const formattedTrigger = { ...triggers };
  Object.keys(formattedTrigger).forEach((key) => {
    formattedTrigger[key] = formattedTrigger[key].filter(
      ({ linked_section }) => !linked_section
    );
    const type = boxes.find((b) => b.id === key)?.type;
    if (type && Box.isATextBox(type))
      formattedTrigger[key] = formattedTrigger[key].map((trigger) => {
        return {
          ...trigger,
          result: trigger.result.hideField
            ? trigger.result
            : contentToRaw(trigger.result),
        };
      });
  });
  return formattedTrigger;
};

const defaultValueToRaw = (box) => {
  if (Box.isATextBox(box.type) || Box.isATitleBox(box.type)) {
    return contentToRaw(box.defaultValue);
  }
  if (
    box.type === ItemTypes.CHECKBOXS_VARIABLE ||
    box.type === ItemTypes.QUESTION
  ) {
    return {
      checkboxs: box.defaultValue.checkboxs.map((c) => ({
        ...c,
        label: JSON.stringify(convertToRaw(c.label.getCurrentContent())),
      })),
    };
  }
  if (box.type === ItemTypes.SELECTOR_VARIABLE) {
    return {
      options: box.defaultValue.options.map((c) => ({
        ...c,
        label: JSON.stringify(convertToRaw(c.label.getCurrentContent())),
      })),
    };
  }
  return box.defaultValue;
};

export const textToRaw = (boxes) =>
  boxes.map((box) => {
    let newBox = { ...box };
    if (Box.isATextBox(box.type) || Box.isATitleBox(box.type)) {
      newBox = contentToRaw(newBox);
    }
    if (isAMultiTextBox(box.type)) {
      newBox = multiTextContentToRaw(newBox);
    }
    // Textes d'une forme composée : dans ses sous-formes et leurs liens (cf.
    // shapeText.js).
    if (box.type === ItemTypes.SHAPE && Array.isArray(box.content?.shapes)) {
      newBox = { ...newBox, content: { ...newBox.content, shapes: serializeShapeTexts(newBox.content.shapes) } };
    }
    if (Box.isAVariableBox(newBox.type)) {
      newBox = {
        ...newBox,
        name: JSON.stringify(convertToRaw(box.name.getCurrentContent())),
      };
      if (
        newBox.type === ItemTypes.CHECKBOXS_VARIABLE ||
        newBox.type === ItemTypes.QUESTION
      ) {
        newBox = {
          ...newBox,
          checkboxs: newBox.checkboxs.map((c) => ({
            ...c,
            label: JSON.stringify(convertToRaw(c.label.getCurrentContent())),
          })),
        };
      }
      if (newBox.type === ItemTypes.SELECTOR_VARIABLE) {
        newBox = {
          ...newBox,
          options: newBox.options.map((c) => ({
            ...c,
            label: JSON.stringify(convertToRaw(c.label.getCurrentContent())),
          })),
        };
      }
    }
    if (newBox.defaultValue) {
      if (newBox.defaultValue.hideField)
        newBox.defaultValue = { hideField: true };
      else newBox.defaultValue = defaultValueToRaw(newBox);
    }
    if (Box.isATableBox(newBox.type)) {
      newBox.isFetching = undefined;
    }
    return newBox;
  });

export const getEditorTextAsHTML = (editorState) =>
  stateToHTML(editorState.getCurrentContent());

export const buildUniqueContentData = (content) => {
  if (content.columns) {
    if (content.columns.some((col) => !_.startsWith(col.key, 'col_'))) {
      content.columns.forEach((col) => {
        if (!_.startsWith(col.key, 'col_')) {
          const legacyKey = col.key;
          const legacyUuid = col._uuid;
          col.key = colId();
          col.legacyUuid = legacyUuid || legacyKey;
        }
      });
    }
  }
  if (content.data.length > 0) {
    // check that data is mapped to column uuids
    if (content.columns) {
      content.data.forEach((row, rowIndex) => {
        content.columns.forEach((column) => {
          if (row[column.key] === undefined) {
            row[column.key] =
              row[column.title] ??
              row[column.legacyUuid] ??
              row[column.legacyKey] ??
              '';
            delete row[column.title];
            delete row[column.legacyUuid];
            delete row[column.legacyKey];
          }
          if (row.hiddenColumnsKeys === undefined) {
            row.hiddenColumnsKeys = [];
          }
          if (column.merge && rowIndex > 0) {
            if (row[column.key] === content.data[rowIndex - 1][column.key]) {
              if (!row.hiddenColumnsKeys.includes(column.key)) {
                row.hiddenColumnsKeys.push(column.key);
              }
            }
          }
        });
        Object.keys(row).forEach((colKey) => {
          // `manual` : ligne ajoutée à la main, à garder sinon elle se refusionne
          // au rechargement (cf. computeLabelRowMerges). `rowKind` : type de
          // ligne donné par l'API (zone), lu par les règles de fusion.
          // `rowStyle` : style de la ligne d'un tableau custom (cf. customRowStyles).
          if (colKey === 'hiddenColumnsKeys' || colKey === 'manual' || colKey === 'rowKind' || colKey === 'rowStyle') {
            return;
          }
          if (colKey === 'key') {
            if (!_.startsWith(row.key, 'row_')) {
              row.key = `row${row.key}`;
            }
          } else if (!_.startsWith(colKey, 'textAlign_')) {
            if (!content.columns.some((col) => col.key === colKey)) {
              delete row[colKey];
            }
          }
        });
      });
      content.columns.forEach((column) => {
        delete column.dataIndex;
        delete column._uuid;
        delete column.legacyUuid;
        delete column.leagcyKey;
      });
    }
  }
  if (!content.columnsDisposition) {
    content.columnsDisposition = [];
  }
  return content;
};

export const optionalRowsSelection = (content) => {
  const { rowsSelection } = content;
  if (rowsSelection) {
    if (rowsSelection[0] > 0) {
      content.rowsSelectionStart = rowsSelection[0];
    }
    if (rowsSelection[1] < content.data.length - 1) {
      content.rowsSelectionEnd = rowsSelection[1];
    }
    delete content.rowsSelection;
  }
  return content;
};

const addUndefinedVariables = ({
  baseKey = '',
  secondKey,
  firstKey,
  variables,
  value = '',
  name,
  currentDocIsTemplate,
  hideEmptyVariables = true,
}) => {
  // Toujours cloner l'objet racine pour éviter de modifier un objet potentiellement figé
  const newVariables = { ...variables };
  const variableKey = `${baseKey}${firstKey}.${secondKey}`;
  let currentVariable = newVariables[variableKey];

  if (typeof currentVariable !== 'object') {
    currentVariable = {
      name: name ?? i18n.t(`${firstKey}.${secondKey}`),
      value: value ?? '',
      notUsed: true,
    };
    if (variableIsAnImage(secondKey)) {
      currentVariable.isImg = true;
    }
    newVariables[variableKey] = currentVariable;
  } else {
    // Cloner la variable existante pour éviter les erreurs sur des objets en lecture seule
    currentVariable = { ...currentVariable };
    newVariables[variableKey] = currentVariable;

    if (currentVariable.value === '') {
      currentVariable.value = value ?? '';
    }
    currentVariable.name = name ?? i18n.t(`${firstKey}.${secondKey}`);
  }
  if (
    currentVariable &&
    typeof currentVariable.value === 'string'
  )
    currentVariable.value = currentVariable.value?.replace(
      /\r/g,
      ''
    );
  if (!currentDocIsTemplate && currentVariable) {
    // Travailler toujours sur une copie de defaultValue pour éviter toute mutation
    // d'un objet potentiellement figé (provenant de l'API ou d'un autre contexte).
    let nextDefaultValue = currentVariable.defaultValue
      ? { ...currentVariable.defaultValue }
      : {};

    if (currentVariable.hideField && !nextDefaultValue.hideField) {
      nextDefaultValue.hideField = true;
    }

    if (typeof value === 'string') {
      nextDefaultValue.value = value.replace(/\r/g, '');
    }

    currentVariable.defaultValue = nextDefaultValue;
  }
  if (hideEmptyVariables) {
    if (currentVariable?.value === '') {
      currentVariable.value = ' ';
    }
    if (currentVariable?.defaultValue?.value === '') {
      currentVariable.defaultValue.value = ' ';
    }
  }
  if (currentDocIsTemplate) {
    if (currentVariable?.defaultValue) {
      delete currentVariable.defaultValue;
    }
  }
  return newVariables;
};

const formatProposalVariables = ({
  variablesToSearch,
  variables,
  baseKey = '',
  currentDocIsTemplate,
  hideEmptyVariables,
}) => {
  let newVariables = variables;
  Object.entries(variablesToSearch).forEach(([firstKey, variablesInfos]) => {
    Object.entries(variablesInfos).forEach(([secondKey, value]) => {
      newVariables = addUndefinedVariables({
        baseKey,
        secondKey,
        firstKey,
        variables: newVariables,
        value,
        currentDocIsTemplate,
        hideEmptyVariables,
      });
    });
  });
  return newVariables;
};

const filterUselessVariables = (variables, defaultVariablesApi) => {
  if (!defaultVariablesApi || !variables) return variables;
  const filtered = {};
  Object.entries(variables).forEach(([key, value]) => {
    // Garder toujours les variables custom.* (portent les remarques et la config définies dans le template)
    if (key.startsWith('custom.')) {
      filtered[key] = { ...value };
      return;
    }

    // Pour les autres variables, on garde :
    // - celles réellement utilisées (notUsed !== true)
    // - ou celles présentes dans defaultVariablesApi (variables d'opportunité / offre…)
    if (!value.notUsed || defaultVariablesApi[key]) {
      filtered[key] = { ...value };
    }
  });
  return filtered;
};

export const formatVariables = ({
  content,
  defaultVariablesApi,
  currentDocIsTemplate,
  hideEmptyVariables = false,
}) => {
  let variables =
    filterUselessVariables(content.variables, defaultVariablesApi) || {};
  const contentVariables = content.variables || {};
  let contentVariableGroups = content.variableGroups ?? [];
  if (!defaultVariablesApi) {
    if (hideEmptyVariables) {
      Object.entries(variables).forEach(([key, variable]) => {
        if (variable.value === '') {
          variables[key].value = ' ';
        }
      });
    }
    return { variables, variableGroups: contentVariableGroups };
  }
  const {
    variable_groups: variableGroups,
    custom_text_variables: customTextVariables,
    custom_text_variable_remarks: customTextVariableRemarks = {},
    custom_text_variable_predefined_values: customTextVariablePredefinedValues = {},
    ...otherVariables
  } = defaultVariablesApi;

  if (currentDocIsTemplate) {
    const baseObj = otherVariables.offer_variables ?? otherVariables;
    Object.entries(baseObj).forEach(([firstKey, variablesNames]) => {
      variablesNames.forEach((secondKey) => {
        variables = addUndefinedVariables({
          secondKey,
          firstKey,
          variables,
          currentDocIsTemplate,
          hideEmptyVariables,
        });
      });
    });
    Object.entries(customTextVariables).forEach(([nameWithId, value]) => {
      const id = nameWithId.substring(nameWithId.lastIndexOf('.') + 1);
      const name = nameWithId.substring(0, nameWithId.lastIndexOf('.'));
      const variableKey = `custom.${id}`;

      variables = addUndefinedVariables({
        secondKey: id,
        firstKey: 'custom',
        variables,
        value,
        name,
        currentDocIsTemplate,
        hideEmptyVariables,
      });

      // Injecter la remarque de la variable custom depuis l'API si disponible
      const remarkFromApi = customTextVariableRemarks[nameWithId];
      if (remarkFromApi && variables[variableKey]) {
        variables[variableKey].remark = remarkFromApi;
      }

      // Les valeurs prédéfinies vivent uniquement en base : l'API fait foi.
      if (variables[variableKey]) {
        variables[variableKey].predefinedValues =
          customTextVariablePredefinedValues[nameWithId] ?? [];
      }
    });
  } else {
    if (otherVariables.offer_variables) {
      variables = formatProposalVariables({
        variablesToSearch: otherVariables.offer_variables,
        variables,
        currentDocIsTemplate,
        hideEmptyVariables,
      });
      otherVariables.opportunities_variables.forEach((oppInfos) => {
        const oppName = Object.keys(oppInfos)[0];
        const baseOpportunityKey = `${oppInfos[oppName].opportunity.CHRONO}.`;
        variables = formatProposalVariables({
          variablesToSearch: oppInfos[oppName],
          variables,
          baseKey: baseOpportunityKey,
          currentDocIsTemplate,
          hideEmptyVariables,
        });
      });
    } else {
      variables = formatProposalVariables({
        variablesToSearch: otherVariables,
        variables,
        currentDocIsTemplate,
        hideEmptyVariables,
      });
    }
    Object.entries(customTextVariables).forEach(([nameWithId, value]) => {
      const id = nameWithId.substring(nameWithId.lastIndexOf('.') + 1);
      const name = nameWithId.substring(0, nameWithId.lastIndexOf('.'));
      const variableKey = `custom.${id}`;

      // Variable éventuellement déjà présente dans le contenu (proposal existante)
      const existingVariable = contentVariables[variableKey];

      variables = addUndefinedVariables({
        secondKey: id,
        firstKey: 'custom',
        variables,
        value,
        name,
        currentDocIsTemplate,
        hideEmptyVariables,
      });

      // Remarque en priorité depuis le contenu (proposal/template),
      // sinon depuis l'API (custom_text_variable_remarks)
      const remarkFromApi = customTextVariableRemarks[nameWithId];
      if (variables[variableKey]) {
        if (existingVariable && typeof existingVariable.remark !== 'undefined') {
          variables[variableKey].remark = existingVariable.remark;
        } else if (remarkFromApi) {
          variables[variableKey].remark = remarkFromApi;
        }
        // Les valeurs prédéfinies vivent uniquement en base : l'API fait foi.
        variables[variableKey].predefinedValues =
          customTextVariablePredefinedValues[nameWithId] ?? [];
      }
    });
  }
  // Non-indexed recursive placeholders ({{model.field}}) are template-authoring helpers only.
  // A proposal carries real data in indexed vars (model.N.field), so a persisted placeholder
  // (saved visible from the template) must never surface in the proposal form.
  if (!currentDocIsTemplate) {
    Object.keys(variables).forEach((key) => {
      const v = variables[key];
      if (
        v?.isRecursive &&
        typeof v.value === 'string' &&
        /^\{\{.*\}\}$/.test(v.value.trim())
      ) {
        delete variables[key];
      }
    });
  }
  variables = Object.entries(variables)
    .sort(([, a], [, b]) => a.name.localeCompare(b.name))
    .reduce((r, [k, v]) => ({ ...r, [k]: v }), {});

  const vGS = Object.entries(variableGroups).map(([key, value]) => {
    const baseVG = {
      ...rawVariableGroupToStoreVariableGroup({ ...value, id: key }),
    };

    // Remarque issue de l'API (champ remarks ou remark)
    const apiRemark = baseVG.remark ?? baseVG.remarks;

    // En mode template, on fusionne avec les éventuels groupes présents dans le contenu
    if (currentDocIsTemplate) {
      const contentVGIdx = contentVariableGroups.findIndex(
        ({ id }) => id === baseVG.id
      );

      // Aucun groupement dans le contenu : on retourne la version issue de l'API
      if (contentVGIdx === -1) {
        return {
          ...baseVG,
          remark: apiRemark,
        };
      }

      const contentVG = contentVariableGroups[contentVGIdx];

      return {
        ...contentVG,
        ...baseVG,
        // On privilégie la remarque du contenu (template) si elle existe,
        // sinon celle fournie par l'API
        remark:
          (contentVG && typeof contentVG.remark !== 'undefined'
            ? contentVG.remark
            : apiRemark),
      };
    }

    // En mode proposal (opportunity / offer), on s'aligne uniquement sur l'API,
    // qui fournit les groupes de variables "de référence" (avec variable_ids et remarks)
    return {
      ...baseVG,
      remark: apiRemark,
    };
  });

  vGS.sort((vA, vB) => {
    const variableGroupIndexA = contentVariableGroups.findIndex(
      ({ id }) => id === vA.id
    );
    const variableGroupIndexB = contentVariableGroups.findIndex(
      ({ id }) => id === vB.id
    );
    if (variableGroupIndexA >= 0 && variableGroupIndexB === -1) return -1;
    if (variableGroupIndexB >= 0 && variableGroupIndexA === -1) return 1;
    if (variableGroupIndexA > variableGroupIndexB) return 1;
    return -1;
  });

  attachAddressGroups({ variables, variableGroups: vGS });

  return {
    variables,
    variableGroups: vGS,
  };
};

export const transformTextToEditorState = (value) => {
  if (!value || (typeof value !== 'string' && typeof value !== 'number')) {
    return EditorState.createEmpty(decorator);
  }
  const text = typeof value === 'number' ? value.toString() : value;
  let editorState;
  try {
    editorState = EditorState.createWithContent(
      convertFromRaw(JSON.parse(text)),
      decorator
    );
    editorState = replaceStyle(editorState, 'BIGGERBOLD', 'BOLD');
  } catch (e) {
    editorState = EditorState.createWithContent(
      ContentState.createFromText(text),
      decorator
    );
  }
  return editorState;
};

// Remplace le texte d'une valeur Draft sérialisée (titre de colonne renommé
// depuis « Configurer le tableau ») : le style de son premier caractère (gras,
// couleur…) passe sur tout le nouveau texte, le reste de la mise en forme part.
export const replaceDraftPlainText = (value, text) => {
  const firstBlock = transformTextToEditorState(value).getCurrentContent().getFirstBlock();
  const style = firstBlock.getLength() > 0 ? firstBlock.getInlineStyleAt(0) : null;
  let content = ContentState.createFromText(text ?? '');
  if (style?.size) {
    const blocks = content.getBlocksAsArray();
    const last = blocks[blocks.length - 1];
    const selection = SelectionState.createEmpty(blocks[0].getKey()).merge({
      focusKey: last.getKey(),
      focusOffset: last.getLength(),
    });
    style.forEach((name) => {
      content = Modifier.applyInlineStyle(content, selection, name);
    });
  }
  return JSON.stringify(convertToRaw(content));
};

// Contenu d'une cellule fusionnée. `values` = contenus des cases sélectionnées
// dans l'ordre de lecture. Cases au texte identique -> affichées une seule fois ;
// cases au texte différent -> cumulées (blocs empilés). Rich text (Draft.js)
// préservé ; les cases vides sont ignorées.
export const computeMergedCellContent = (values) => {
  const seen = new Set();
  const distinct = [];
  (values || []).forEach((value) => {
    const text = convertContentToRawText(transformTextToEditorState(value)).trim();
    if (!text) return; // ignore les cases vides
    if (seen.has(text)) return; // dédoublonne les identiques
    seen.add(text);
    distinct.push(value);
  });
  if (distinct.length === 0) return '';
  if (distinct.length === 1) return distinct[0];

  // Cumul : concatène les ContentState en réindexant les entités (liens/images).
  const blocks = [];
  const entityMap = {};
  let entityCounter = 0;
  distinct.forEach((value) => {
    const raw = convertToRaw(transformTextToEditorState(value).getCurrentContent());
    const localRemap = {};
    Object.keys(raw.entityMap).forEach((oldKey) => {
      const newKey = String(entityCounter++);
      entityMap[newKey] = raw.entityMap[oldKey];
      localRemap[oldKey] = Number(newKey);
    });
    raw.blocks.forEach((block) => {
      blocks.push({
        ...block,
        key: genKey(),
        entityRanges: (block.entityRanges || []).map((range) => ({
          ...range,
          key: localRemap[range.key],
        })),
      });
    });
  });
  return JSON.stringify({ blocks, entityMap });
};

const formatVariableBoxValue = ({
  boxType,
  boxValue,
  currentDocIsTemplate,
  variables,
}) => {
  if (
    boxType === ItemTypes.CHECKBOXS_VARIABLE ||
    boxType === ItemTypes.QUESTION
  ) {
    boxValue.checkboxs = boxValue.checkboxs.map((c) => ({
      ...c,
      label: transformTextToEditorState(c.label),
    }));
  }

  if (boxType === ItemTypes.SELECTOR_VARIABLE) {
    boxValue.options = boxValue.options.map((c) => ({
      ...c,
      label: transformTextToEditorState(c.label),
    }));
  }

  if (!currentDocIsTemplate && variables) {
    if (
      boxType === ItemTypes.CHECKBOXS_VARIABLE ||
      boxType === ItemTypes.QUESTION
    ) {
      boxValue.checkboxs = boxValue.checkboxs.map((c) => ({
        ...c,
        label: replaceVariables(c.label, variables),
      }));
    }

    if (boxType === ItemTypes.SELECTOR_VARIABLE) {
      boxValue.options = boxValue.options.map((c) => ({
        ...c,
        label: replaceVariables(c.label, variables),
      }));
    }
  }
  return boxValue;
};

const formatVariableBox = ({ oldBox, currentDocIsTemplate, variables }) => {
  let box = oldBox;
  box.name = transformTextToEditorState(box.name);
  box.remark = JSON.stringify(
    convertToRaw(
      replaceVariables(
        transformTextToEditorState(box.remark),
        variables
      ).getCurrentContent()
    )
  );

  box = {
    ...box,
    ...formatVariableBoxValue({
      boxType: box.type,
      boxValue: box,
      currentDocIsTemplate,
      variables,
    }),
  };

  if (!currentDocIsTemplate && variables) {
    box.name = replaceVariables(box.name, variables);
  }
  return box;
};

const formatTextContent = ({
  contentText,
  currentDocIsTemplate,
  variables,
}) => {
  let editorState = EditorState.createWithContent(
    convertFromRaw(JSON.parse(contentText.raw)),
    decorator
  );
  editorState = replaceVariablesInEditorState(
    editorState,
    variables,
    currentDocIsTemplate
  );
  editorState = replaceVariables(editorState, variables, currentDocIsTemplate);
  editorState = replaceStyle(editorState, 'BIGGERBOLD', 'BOLD');
  return {
    ...contentText,
    editorState,
    text: getEditorTextAsHTML(editorState),
  };
};

const formatTriggers = ({
  triggers,
  currentDocIsTemplate,
  variables,
  boxes,
}) => {
  const formattedTrigger = triggers ?? {};
  Object.keys(formattedTrigger).forEach((key) => {
    const type = boxes.find((b) => b.id === key)?.type;
    if (type && Box.isATextBox(type)) {
      formattedTrigger[key] = formattedTrigger[key].map((trigger) => {
        return {
          ...trigger,
          result: trigger.result.hideField
            ? trigger.result
            : {
                content: formatTextContent({
                  contentText: trigger.result.content,
                  currentDocIsTemplate,
                  variables,
                }),
              },
        };
      });
    }
  });
  return formattedTrigger;
};

const formatContent = ({
  boxType,
  content,
  currentDocIsTemplate,
  variables,
  defaultVariablesApi,
  documentType,
}) => {
  let boxContent;
  if (Box.isATextBox(boxType)) {
    boxContent = formatTextContent({
      contentText: content,
      currentDocIsTemplate,
      variables,
    });
  } else if (Box.isATitleBox(boxType)) {
    let editorState;
    if (content.raw) {
      editorState = EditorState.createWithContent(
        convertFromRaw(JSON.parse(content.raw)),
        decorator
      );
    } else {
      editorState = EditorState.createWithContent(
        ContentState.createFromText(content.text),
        decorator
      );
    }
    editorState = replaceVariablesInEditorState(
      editorState,
      variables,
      currentDocIsTemplate
    );
    editorState = replaceVariables(editorState, variables, currentDocIsTemplate);
    boxContent = {
      ...content,
      indexTitle:
        content.indexTitle && _.isArray(content.indexTitle)
          ? content.indexTitle
          : createIndexArray(content.indexTitle),
      editorState,
    };
  } else if (isAMultiTextBox(boxType)) {
    // Chemin inverse de multiTextContentToRaw : chaque texte retrouve son
    // EditorState, variables résolues comme pour un titre.
    boxContent = { ...content };
    MULTI_TEXT_CONTENT_KEYS[boxType].forEach((key) => {
      const raw = content?.[`${key}Raw`];
      let editorState = raw
        ? EditorState.createWithContent(
            convertFromRaw(JSON.parse(raw)),
            decorator
          )
        : EditorState.createEmpty(decorator);
      editorState = replaceVariablesInEditorState(
        editorState,
        variables,
        currentDocIsTemplate
      );
      editorState = replaceVariables(editorState, variables, currentDocIsTemplate);
      boxContent[`${key}EditorState`] = editorState;
    });
  } else if (boxType === ItemTypes.SHAPE && Array.isArray(content?.shapes)) {
    // Chemin inverse de serializeShapeTexts, variables résolues comme pour un
    // texte de volet.
    boxContent = {
      ...content,
      shapes: hydrateShapeTexts(content.shapes, (raw) => {
        let editorState = EditorState.createWithContent(convertFromRaw(raw), decorator);
        editorState = replaceVariablesInEditorState(editorState, variables, currentDocIsTemplate);
        return replaceVariables(editorState, variables, currentDocIsTemplate);
      }),
    };
  } else if (Box.isAImgBox(boxType)) {
    if (!currentDocIsTemplate && defaultVariablesApi) {
      boxContent = {
        ...content,
        src: replaceVariablesInPlainText(content.src, defaultVariablesApi),
      };
    }
  } else if (Box.isATableBox(boxType)) {
    boxContent = buildUniqueContentData(content);
    boxContent = optionalRowsSelection(boxContent);
    boxContent = replacePlaceholderTable(boxContent, variables, documentType);
  }
  return boxContent;
};

export const formatBoxes =
  (
    defaultVariablesApi,
    currentDocIsTemplate,
    documentType,
    pageToColumnDict,
    pageNumber,
    variables
  ) =>
  (box) => {
    const boxContent = formatContent({
      boxType: box.type,
      content: box.content,
      currentDocIsTemplate,
      variables,
      defaultVariablesApi,
      documentType,
    });

    if (Box.isAVariableBox(box.type)) {
      box = formatVariableBox({ oldBox: box, currentDocIsTemplate, variables });
    }
    if (box.defaultValue && !box.defaultValue.hideField) {
      const content = formatContent({
        boxType: box.type,
        content: box.content,
        currentDocIsTemplate,
        variables,
        defaultVariablesApi,
        documentType,
      });
      box.defaultValue = {
        ...box.defaultValue,
        content,
      };
      box.defaultValue = {
        ...box.defaultValue,
        ...formatVariableBoxValue({
          boxType: box.type,
          boxValue: box.defaultValue,
          currentDocIsTemplate,
          variables,
        }),
      };
    }
    box = infinitePageBox(box, pageToColumnDict, pageNumber);
    return syncBoxStructure(box, boxContent);
  };

const formatMasks = ({
  name,
  id,
  boxes,
  defaultVariablesApi,
  currentDocIsTemplate,
  documentType,
  editableMask,
  variables,
  hasNotPageNumber,
  chapterSeparation,
  noRepeat,
}) => {
  const maskBoxes = boxes.map((box) => {
    if (!box.id) {
      if (box.idForMask) {
        box.id = box.idForMask;
      } else {
        console.error('box without id or idForMask', box);
      }
    }
    return formatBoxes(
      defaultVariablesApi,
      currentDocIsTemplate,
      documentType,
      null,
      null,
      variables
    )(box);
  });
  return {
    name,
    id,
    editableMask,
    boxes: maskBoxes.filter((box) => box.id),
    hasNotPageNumber:
      typeof hasNotPageNumber === 'undefined' && id === 'cover'
        ? true
        : hasNotPageNumber,
    ...(chapterSeparation !== undefined && { chapterSeparation }),
    ...(noRepeat !== undefined && { noRepeat }),
  };
};

const formatFontFamily = ({ content }) => {
  let fontFamilyList = [];
  if (content?.configuration?.fontFamilyList?.length > 0) {
    fontFamilyList = content.configuration.fontFamilyList.filter(
      (font) => _.isString(font) || (font.key && font.familyName)
    );
    fontFamilyList = content.configuration.fontFamilyList.map((font) => {
      if (_.isString(font)) {
        return { key: font, familyName: font };
      } else {
        return font;
      }
    });
  }
  return fontFamilyList;
};

export const formatResContent = (
  content,
  defaultVariablesApi,
  currentDocIsTemplate = false,
  landscape = false,
  hideEmptyVariables = false
) => {
  const documentType = content.documentType;
  const formattedBoxes = [];
  let formattedContainer = [];
  let formattedColumns = [];
  const pageToColumnDict = {};
  const pageNumber = content.pages?.length || 0;

  const {
    variables: formattedVariables,
    variableGroups: formattedVariableGroups,
  } = formatVariables({
    content,
    defaultVariablesApi,
    currentDocIsTemplate,
    hideEmptyVariables,
  });

  let masks = content.masks.map(withoutPageBackgroundFill).map(
    ({ name, id, boxes, editableMask, hasNotPageNumber, chapterSeparation, noRepeat, ...maskProps }) => ({
      ...maskProps,
      ...formatMasks({
        name,
        id,
        editableMask,
        boxes,
        defaultVariablesApi,
        currentDocIsTemplate,
        documentType,
        hasNotPageNumber,
        chapterSeparation,
        noRepeat,
        variables: formattedVariables,
      }),
    })
  );

  content.pages?.forEach((p, idx) => {
    const newCol = newColumn({
      size: 1,
      drawMode: true,
      isOldPage: true,
      backgroundColor: p.backgroundColor.style,
    });
    const newCont = newContainer({
      height: landscape ? pageWidth : pageHeight,
      columnsIds: [newCol.id],
    });
    formattedContainer.push(newCont);
    formattedColumns.push(newCol);
    pageToColumnDict[p.id] = newCol.id;
  });

  if (content.containers?.length > 0) {
    formattedContainer = formattedContainer.concat(content.containers);
  }

  if (content.columns?.length > 0) {
    formattedColumns = formattedColumns.concat(content.columns);
  }

  content.boxes.forEach((box) => {
    let formattedBox = formatBoxes(
      defaultVariablesApi,
      currentDocIsTemplate,
      documentType,
      pageToColumnDict,
      pageNumber,
      formattedVariables
    )(box);
    formattedBoxes.push(formattedBox);
  });

  const fontFamilyList = formatFontFamily({ content });
  let variableOrderArray = content.variableOrderArray ?? [];

  if (!variableOrderArray || variableOrderArray.length < 1) {
    const variablesGroupedByContainer = getVariablesGroupedByContainer({
      variableGroups: formattedVariableGroups,
      variables: formattedVariables,
      boxes: [...formattedBoxes, ...masks.map((m) => m.boxes).flat()],
      containers: formattedContainer,
      columns: formattedColumns,
      rowContainers: content.rowContainers ?? [],
    });
    const array = variablesGroupedByContainer
      .filter(
        (vInfos) =>
          vInfos.type === variableHandlingType.FORM ||
          vInfos.variablesBoxes.some((b) => !b.notUsed)
      )
      .map((vInfos) => {
        if (vInfos.type === variableHandlingType.FORM)
          return vInfos.variablesBoxes.map((b) => ({
            ...b,
            canDrag: false,
            containerId: vInfos.containerId,
          }));
        if (vInfos.variableGroupBox)
          return {
            ...vInfos.variableGroupBox,
            containerId: vInfos.containerId,
          };
        return vInfos.variablesBoxes.map((b) => ({
          ...b,
          containerId: vInfos.containerId,
        }));
      })
      .flat();
    variableOrderArray = array.map((v) => ({
      id: v.id,
      containerId: v.containerId,
    }));
  }

  return {
    ...content,
    configuration: {
      ...content.configuration,
      fontFamilyList,
    },
    boxes: formattedBoxes,
    masks,
    containers: formattedContainer,
    columns: formattedColumns,
    // Toujours présent : applyProposal n'écrase que les clés du content, un
    // document sans lignes laisserait sinon celles du document précédent.
    rowContainers: content.rowContainers ?? [],
    variables: formattedVariables,
    variableGroups: formattedVariableGroups,
    variableOrderArray,
    triggers: formatTriggers({
      triggers: content.triggers,
      currentDocIsTemplate,
      variables: formattedVariables,
      boxes: formattedBoxes,
    }),
  };
};

// export const syncPageStructure = (pages = [newPage()]) => {
//   return pages.map((page) => {
//     const defaultValues = _.merge({}, defaultPageStructure);
//     return _.merge(defaultValues, page);
//   });
// };

export const syncStructure = (structureToSync, defaultStructure) => {
  let defaultValues = _.merge({}, defaultStructure);
  if (structureToSync) {
    const defaultValuesWithoutEditorState = _.omit(
      defaultValues,
      'text.content.editorState'
    );
    try {
      return _.merge(defaultValuesWithoutEditorState, structureToSync);
    } catch (error) {
      return defaultValuesWithoutEditorState;
    }
  } else {
    return _.merge(defaultValues, structureToSync);
  }
};

export const infinitePageBox = (boxFromAPI, pageToColumnDict, pageNumber) => {
  const infiniteBox = {
    ...boxFromAPI,
  };
  if (
    !boxFromAPI.infiniteCompliant &&
    typeof boxFromAPI.pageNumber === 'number'
  ) {
    infiniteBox.infiniteCompliant = true;
    // Page data cleanup
    if (infiniteBox.hasOwnProperty('pageId')) {
      if (pageToColumnDict) {
        infiniteBox.columnId = pageToColumnDict[infiniteBox.pageId];
      }
      delete infiniteBox.pageId;
    }
    if (infiniteBox.hasOwnProperty('pageUuid')) {
      delete infiniteBox.pageUuid;
    }
    if (infiniteBox.hasOwnProperty('pageNumber')) {
      delete infiniteBox.pageNumber;
    }
    // // Mask data cleanup
    if (infiniteBox.hasOwnProperty('currentMask')) {
      delete infiniteBox.currentMask;
    }
  }
  return infiniteBox;
};

// -------------------------------------------------
// HERE ☝️
// -------------------------------------------------

export const syncBoxStructure = (boxFromAPI, boxContent = null) => {
  const defaultBox = _.merge({}, defaultItemValues[boxFromAPI.type]);
  const boxTypeDefaultValues = _.omit(defaultBox, 'content');
  const boxWithoutContent = _.omit(boxFromAPI, 'content');
  const completeBox = _.merge(boxTypeDefaultValues, boxWithoutContent);
  completeBox.content = boxContent ?? boxFromAPI.content;
  return completeBox;
};

export const variablesNames = (variables) => {
  if (!variables) return [];
  return Object.keys(variables)
    .map((variableGroup) => {
      return Object.keys(variables[variableGroup]).map((variableName) => {
        return `${variableGroup}.${variableName}`;
      });
    })
    .flat();
};

export const formatTemplatePattern = (templatePattern) => {
  if (templatePattern) {
    return 'section';
  } else if (templatePattern === 'style') {
    return 'style';
  } else {
    return 'template';
  }
};
